import { useEffect, useRef, useState } from 'react';

import type { ThumbnailMetadata } from '../utils/videoUtils';

interface UseThumbnailExtractorProps {
  videoSrc: string | null;
  thumbnails: ThumbnailMetadata[];
}

interface UseThumbnailExtractorReturn {
  /** Blob URL for each thumbnail index, or null if not yet captured. */
  frameUrls: (string | null)[];
  isExtracting: boolean;
  /** True if canvas capture failed (e.g. CORS); caller should fall back to <video>. */
  hasError: boolean;
}

/**
 * Extracts filmstrip thumbnail frames using a single hidden <video> + <canvas>
 * instead of N separate <video> elements. Seeks sequentially and updates state
 * after each frame so thumbnails appear progressively.
 */
export function useThumbnailExtractor({
  videoSrc,
  thumbnails,
}: UseThumbnailExtractorProps): UseThumbnailExtractorReturn {
  const [frameUrls, setFrameUrls] = useState<(string | null)[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const blobUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!videoSrc || thumbnails.length === 0) {
      setFrameUrls((prev) => (prev.length === 0 ? prev : []));
      setIsExtracting(false);
      setHasError(false);
      return;
    }

    let cancelled = false;
    const capturedUrls: string[] = [];

    const extract = async () => {
      setIsExtracting(true);
      setHasError(false);
      setFrameUrls(new Array(thumbnails.length).fill(null));

      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.preload = 'auto';
      video.src = videoSrc;

      try {
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(
            () => reject(new Error('Metadata timeout')),
            10000
          );
          video.addEventListener(
            'loadedmetadata',
            () => {
              clearTimeout(timeout);
              resolve();
            },
            { once: true }
          );
          video.addEventListener(
            'error',
            () => {
              clearTimeout(timeout);
              reject(new Error('Video load error'));
            },
            { once: true }
          );
        });

        if (cancelled) return;

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas context unavailable');

        for (let i = 0; i < thumbnails.length; i++) {
          if (cancelled) return;

          video.currentTime = thumbnails[i].timeInSeconds;

          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(
              () =>
                reject(
                  new Error(`Seek timeout at ${thumbnails[i].timeInSeconds}s`)
                ),
              5000
            );
            video.addEventListener(
              'seeked',
              () => {
                clearTimeout(timeout);
                resolve();
              },
              { once: true }
            );
          });

          if (cancelled) return;

          ctx.drawImage(video, 0, 0);

          const blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.7);
          });

          if (cancelled || !blob) continue;

          const url = URL.createObjectURL(blob);
          capturedUrls.push(url);

          setFrameUrls((prev) => {
            const next = [...prev];
            next[i] = url;
            return next;
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Thumbnail extraction failed:', error);
          setHasError(true);
        }
      } finally {
        video.src = '';
        video.load();
        if (!cancelled) {
          setIsExtracting(false);
          blobUrlsRef.current = capturedUrls;
        }
      }
    };

    extract();

    return () => {
      cancelled = true;
      blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      blobUrlsRef.current = [];
      capturedUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [videoSrc, thumbnails]);

  return { frameUrls, isExtracting, hasError };
}
